/**
 * Component: Klevu Search
 * ------------------------------------------------------------------------------
 * Custom quick search modal
 *
 * @namespace klevuSearch
 */
import axios from 'axios'
import {formatMoney} from '@shopify/theme-currency'
import {on, percentageSaving} from '../helpers/utils'
import { garageService, garageServiceReady } from '../services/garageService'
import XMLParser from '../vendors/fastXmlParser'

import log from 'salvo-lite/log'

class KlevuSearch {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            searchToggleButton: '[data-klavu-toggle]',
            klevuInput: '[data-klevu-search-input]',
            klevuQuickLink: 'data-klevu-quick-link',
            klevuTotalResults: '.klevu-search__total-results',
            ...JSON.parse(elem.getAttribute('data-klevu-search') || '{}')
        }

        /**
         * DOM node selectors.
         */
        this._searchToggleButton = document.querySelector(this._settings.searchToggleButton)
        this._klevuInput = document.querySelector(this._settings.klevuInput)
        this._searchBar = document.querySelector('.search-bar__bar[data-search="input"]')

        /**
         * Properties.
         */
        this._searchEndpoint = window.theme.klevuSearchEndpoint
        this._searchApikey = window.theme.klevuSearchApi
        this._presetCarFilterArray = []
        this._hiddenCategoriesArray = []
        this._activeVehicleReg = null

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    async onInit () {
        await garageServiceReady
        // Set current vehicle on init
        await this._setCurrentVehicle()

        // Listen for vehicle changes
        garageService.onActiveVehicleChange.subscribe((v) => {
            if (v === null) return
            if (this._activeVehicle === null || this._activeVehicleReg !== v.vin) {
                this._activeVehicleReg = v.vin
                this._setCurrentVehicle(v)
                this._setInitState()
            }
        })

        this._setInitState()
        this._setTriggerEvents()
    }

    async _setCurrentVehicle (updatedVehicle = null) {
        this._presetCarFilterArray = []
        const currentCar = (updatedVehicle === null) ? window._esc_GarageService._activeVehicle : updatedVehicle

        // Clear cache if the vehicle has changed
        if (updatedVehicle !== null) {
            window.localStorage.removeItem('lookup_api_response')
        }

        if (currentCar) {
            // const reg = window._esc_GarageService._activeVehicle.vin.replace(/\s/g, '')
            if (window.localStorage.getItem('lookup_api_response')) {
                this._currentCar = JSON.parse(window.localStorage.getItem('lookup_api_response'))
            } else {
                // Not needed now with search functionality change
                // const data = await this._getVehicleData(reg)
                // this._currentCar = this._convertResponseToCurrentCar(data)
                // window.localStorage.setItem('lookup_api_response', JSON.stringify(this._currentCar))
            }

            this._constructCarFilter(this._currentCar)
        }
    }

    /**
     * Removes fluff from outputs text.
     * @param {String} value - Original response value.
     * @returns {String} - Cleaned up value.
     */
    _individualResultTextFormatter (value) {
        let newValue = value.split('][')
        newValue = newValue[1]
        newValue = newValue.slice(0, -1)

        return newValue
    }

    /**
     * Removes fluff and outputs code.
     * @param {String} value - Original response value.
     * @returns {String} - Cleaned up value.
     */
    _individualResultCodeFormatter (value) {
        let newValue = value.split('][')
        newValue = newValue[0]
        newValue = newValue.substr(1)

        return newValue
    }

    /**
     * Convert the JSON response into the current car object.
     * @param {JSON} response - JSON VIN results.
     * @returns {Object} - Current car object.
     */
    _convertResponseToCurrentCar (response) {
        // Switched from xml-js to fast-xml-parser to resolve dependency versioning issue
        const parser = new XMLParser({
            ignoreDeclaration: true, 
            ignoreAttributes: false, 
            attributeNamePrefix: "_", 
            alwaysCreateTextNode: true, 
            textNodeName: '_text'
        });
        let lookupResults = parser.parse(response.data)

        if (lookupResults.vinDecodeResults) {
            lookupResults = lookupResults.vinDecodeResults.vinDecodeResult
        } else if (lookupResults.regnDecodeResults) {
            lookupResults = lookupResults.regnDecodeResults.regnDecodeResult
        }

        const currentCar = {}
        const yearRegex = /(\s+[0-9-]{5,9})/g

        let vehicleLine = this._individualResultTextFormatter(lookupResults.VehicleLine._text)
        vehicleLine = vehicleLine.replace(' -', '-').replace(' - ', '-')

        /**
         * Test to see if the vehicleLine has a year.
         */
        if (vehicleLine.match(yearRegex)) {
        /**
         * Can't just split on the space in case a model name contains a space.
         * So the below uses Regex to recognise date format then adds character to split on.
         */
            vehicleLine = vehicleLine.replace(yearRegex, '||$1')
            vehicleLine = vehicleLine.split('||')

            currentCar.model = vehicleLine[0]
            currentCar.year = vehicleLine[1].replace(/\s+/g, '')

        /**
         * Else only use the model, this is because some results have no year.
         */
        } else {
            currentCar.model = vehicleLine
        }

        /**
         * Colour.
         */
        currentCar.colour = this._individualResultTextFormatter(lookupResults.ExteriorPaint._text)

        /**
         * Service plan data.
         */
        currentCar.emissionCode = this._individualResultCodeFormatter(lookupResults.EmissionRequirements._text)
        currentCar.engineCode = this._individualResultCodeFormatter(lookupResults.EngineType._text).substring(2, 4)
        currentCar.productionDate = lookupResults.ProductionDate._text
        if (currentCar.registration) { currentCar.registration = lookupResults.RegistrationNumber._text }
        currentCar.vehicleCode = this._individualResultCodeFormatter(lookupResults.VehicleLine._text)
        currentCar.vinNumber = lookupResults.VIN._text
        currentCar.transmissionCode = this._individualResultCodeFormatter(lookupResults.Transmission._text)

        /**
         * Current car method.
         */
        currentCar.method = 'lookup'

        return currentCar
    }

    /**
     * Get vehicle data from API
     * @param {string} searchValue
     */
    async _getVehicleData (searchValue) {
        let searchUrl = ''

        if (searchValue.length > 7) {
            searchUrl = `https://${_charle_url_oec}/api/v1/vin/${searchValue}`;
        } else {
            searchUrl = `https://${_charle_url_oec}/api/v1/reg/${searchValue}`;
        }

        return new Promise((resolve, reject) => {
            axios.get(searchUrl)
                .then((response) => resolve(response))
                .catch((error) => reject(error))
        })
    }

    /**
     * Construct car model filter.
     * @param {Object} currentCar - Parsed car cookie object.
     */
    _constructCarFilter (currentCar) {
        if (currentCar.year) {
            const presentYearString = ` - ${window.theme.strings.currentCar.presentYear}`

            const carModel = currentCar.model.replace(/ /g, '-')
            let yearFormatted = `_${currentCar.year.replace(presentYearString, '').replace(/ /g, '')}`

            if (yearFormatted[yearFormatted.length - 1] === '-') {
                yearFormatted = yearFormatted.slice(0, -1)
            }

            let currentCarTag = carModel + yearFormatted
            currentCarTag = currentCarTag.replace('Ecosport', 'EcoSport').replace('Ka+', 'KA+')

            this._presetCarFilterArray.push({key: 'Filter_Model', values: ['All_Model', currentCarTag]})
        }
    }

    /**
     * Set init state.
     */
    _setInitState () {
        axios({
            method: 'post',
            url: this._searchEndpoint,
            data: {
                context: {
                    apiKeys: [
                        this._searchApikey
                    ]
                },
                recordQueries: [
                    {
                        id: 'allProducts',
                        typeOfRequest: 'SEARCH',
                        settings: {
                            query: {
                                term: '*'
                            },
                            limit: 6,
                            typeOfRecords: ['KLEVU_PRODUCT'],
                            fields: ['id', 'startPrice', 'price', 'url', 'image', 'name', 'salePrice', 'tags'],
                            typeOfSearch: 'DEFAULT',
                            sort: 'RELEVANCE'
                        },
                        filters: {
                            applyFilters: {
                                filters: this._presetCarFilterArray
                            }
                        }
                    }
                ]
            }
        })
            .then(({data}) => {
                this._elem.innerHTML = this._renderTrendingTemplate(data)
                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
     * Set trigger listeners.
     */
    _setTriggerEvents () {
        on('keyup', this._klevuInput, () => {
            this._runSearch()
        })

        on('click', this._searchToggleButton, () => {
            this._klevuInput.focus()
            const inputValue = this._klevuInput.value
            this._klevuInput.value = ''
            this._klevuInput.value = inputValue
        })

        on('click', document, (event) => {
            if (event.target.hasAttribute(this._settings.klevuQuickLink)) {
                this._sendKlevuAnalyticsData(event)
            }
        })

        on('click', this._searchBar, () => {
            this._klevuInput.focus()
        })
    }

    /**
     * Get search data.
     */
    _runSearch () {
        const searchTerm = this._klevuInput.value

        if (searchTerm.length > 0) {
            axios({
                method: 'post',
                url: this._searchEndpoint,
                data: {
                    context: {
                        apiKeys: [
                            this._searchApikey
                        ]
                    },
                    recordQueries: [
                        {
                            id: 'productSearch',
                            typeOfRequest: 'SEARCH',
                            settings: {
                                query: {
                                    term: searchTerm
                                },
                                limit: 6,
                                typeOfRecords: ['KLEVU_PRODUCT'],
                                fields: ['id', 'startPrice', 'price', 'url', 'image', 'name', 'salePrice', 'tags'],
                                typeOfSearch: 'DEFAULT',
                                sort: 'RELEVANCE'
                            },
                            filters: {
                                filtersToReturn: {
                                    enabled: true,
                                    rangeFilterSettings: [
                                        {
                                            key: 'brand'
                                        }
                                    ]
                                },
                                applyFilters: {
                                    filters: this._presetCarFilterArray
                                }
                            }
                        },
                        {
                            id: 'categorySearch',
                            typeOfRequest: 'SEARCH',
                            groupBy: 'NAME',
                            settings: {
                                query: {
                                    term: searchTerm
                                },
                                limit: 3,
                                typeOfRecords: ['KLEVU_CATEGORY'],
                                fields: ['id', 'image', 'name', 'url', 'typeOfRecord'],
                                typeOfSearch: 'DEFAULT',
                                sort: 'RELEVANCE'
                            }
                        }
                    ],
                    suggestions: [{
                        id: 'autocomplete',
                        typeOfQuery: 'AUTO_SUGGESTIONS',
                        query: searchTerm,
                        limit: 5,
                        includeFilters: true
                    }]
                }
            })
                .then(({data}) => {
                    this._elem.innerHTML = this._renderTemplate(data)
                    return data
                })
                .catch((error) => {
                    throw new Error('Could not load data', error)
                })
        } else {
            this._elem.innerHTML = ''
            this._setInitState()
        }
    }

    /**
         * Build quick search template.
         * @param {Object} data - Search query data.
         * @returns {String}
         */
    _renderTemplate (data) {
        return `
            <div class="klevu-search__inner">
            <div class="klevu-search__product-suggestions">
                <h3 class="klevu-search__title">
                ${window.theme.strings.search.titles.products}
                </h3>
    
                ${this._renderProductsTemplate(data.queryResults[0])}
                ${this._renderPromoBox('mobile')}
            </div>
    
            <div class="klevu-search__category-suggestions">
                <h3 class="klevu-search__title klevu-search__title--categories">
                ${window.theme.strings.search.titles.categories}
                </h3>
    
                ${this._renderCategoryTemaplate(data.queryResults[1])}
            </div>
    
            <div class="klevu-search__auto-suggestions">
                <h3 class="klevu-search__title">
                ${window.theme.strings.search.titles.suggestions}
                </h3>
    
                ${this._renderAutoSuggestionsTemplate(data.suggestionResults[0])}
                ${this._renderPromoBox('desktop')}
            </div>
            </div>
        `
    }

    /**
         * Build products template.
         * @param {Object} queryResults - Search query data.
         * @returns {String}
         */
    _renderProductsTemplate (queryResults) {
        const totalProductCount = window.theme.strings.search.titles.viewAllProducts
        const searchTerm = this._klevuInput.value
        const encodedSearchTerm = encodeURIComponent(searchTerm.trim())

        if (queryResults.meta.totalResultsFound === 0) {
            return `
            <div class="klevu-search__no-results">
                ${window.theme.strings.search.titles.noProductsFound}
            </div>
            `
        } else {
            return `
            <ul class="klevu-search__product-list">
                ${this._renderProductSuggestions(queryResults)}
            </ul>
    
            <a
                class="button button--gux klevu-search__total-results"
                href="/search?q=${encodedSearchTerm}"
                target="_parent"
                data-total="${queryResults.meta.totalResultsFound}"
                data-type="${queryResults.meta.typeOfSearch}"
            >
                ${totalProductCount.replace('{{ product_count }}', queryResults.meta.totalResultsFound)}
            </a>
            `
        }
    }

    /**
         * Build suggested products template.
         * @param {Object} queryResults - Search query data.
         * @returns {String}
         */
    _renderProductSuggestions (queryResults) {
        let html = ''

        queryResults.records.forEach((record) => {
            html += `
            <li>
                <a
                class="klevu-search__product-link"
                href="${record.url}"
                data-id="${record.id}"
                data-name="${record.name}"
                js-klevu-quick-link
                >
                <div
                    class="klevu-search__product-image"
                    style="background-image: url('${record.image}')"
                >
                    ${this._renderProductBadge(record)}
                </div>
    
                <span class="klevu-search__product-name">
                    ${record.name}
                </span>
    
                <div class="klevu-search__product-price">
                    ${this._renderPrice(record)}
                </div>
                </a>
            </li>
            `
        })

        return html
    }

    /**
         * Build category template.
         * @param {Object} queryResults - Search query data.
         * @returns {String}
         */
    _renderCategoryTemaplate (queryResults) {
        if (queryResults.meta.totalResultsFound === 0) {
            return `
            <div class="klevu-search__no-results">
                ${window.theme.strings.search.titles.noCategoriesFound}
            </div>
            `
        } else {
            return `
            <ul class="klevu-search__category-list">
                ${this._renderCategoryList(queryResults)}
            </ul>
            `
        }
    }

    /**
         * Build suggested categories template.
         * @param {Object} categoryResults - Search query data.
         * @returns {String}
         */
    _renderCategoryList (categoryResults) {
        let html = ''

        categoryResults.records.forEach((record) => {
            if (this._hiddenCategoriesArray.indexOf(record.name) > -1) {
                return
            }

            html += `
            <li class="klevu-search__list-item">
                <a class="klevu-search__category" href="${record.url}">
                <div
                    class="klevu-search__category-image"
                    style="background-image: url('${record.image}')"
                ></div>
                <span class="klevu-search__category-name">${record.name}</span>
                </a>
            </li>
            `
        })

        return html
    }

    /**
         * Build auto suggestions template.
         * @param {Object} suggestionResults - Search query data.
         * @returns {String}
         */
    _renderAutoSuggestionsTemplate (suggestionResults) {
        if (suggestionResults.suggestions.length === 0) {
            return `
            <div class="klevu-search__no-results">
                ${window.theme.strings.search.titles.noSuggestionsFound}
            </div>
            `
        } else {
            return `
            <ul class="klevu-search__suggestions-list">
            ${this._renderAutoSuggestions(suggestionResults)}
            </ul>
            `
        }
    }

    /**
         * Build auto suggestions template.
         * @param {Object} suggestionResults - Search query data.
         * @returns {String}
         */
    _renderAutoSuggestions (suggestionResults) {
        let html = ''

        suggestionResults.suggestions.forEach((suggestion) => {
            const stripedSuggestName = suggestion.suggest.replace(/<[^>]*>?/gm, '')
            const encodedSuggestName = encodeURIComponent(stripedSuggestName.trim())

            html += `
            <li>
                <a
                class="klevu-search__suggestions-link"
                href="/search?q=${encodedSuggestName}"
                >
                ${suggestion.suggest}
                </a>
            </li>
            `
        })

        return html
    }

    /**
         * Build trending products template.
         * @param {Object} data - Search query data.
         * @returns {String}
         */
    _renderTrendingTemplate (data) {
        return `
            <div class="klevu-search__inner">
            <div
                class="
                klevu-search__product-suggestions
                klevu-search__product-suggestions--custom
                "
            >
                <h3 class="klevu-search__title klevu-search__title--trending">
                ${window.theme.strings.search.titles.trendingProducts}
                </h3>
    
                <ul class="klevu-search__product-list klevu-search__product-list--trending">
                ${this._renderTrendingProducts(data.queryResults[0])}
                </ul>
    
                ${this._renderPromoBox('mobile')}
            </div>
    
            <div class="klevu-search__popular-searches">
                <h3 class="klevu-search__title klevu-search__title--popular-searches">
                ${window.theme.strings.search.titles.popularSearches}
                </h3>
    
                <ul class="klevu-search__popular-list">
                ${this._renderPopularSearches()}
                </ul>
    
                ${this._renderPromoBox('desktop')}
            </div>
            </div>
        `
    }

    /**
         * Build individual trending product template.
         * @param {Object} trendingProducts - Search query data.
         * @returns {String}
         */
    _renderTrendingProducts (trendingProducts) {
        let html = ''

        trendingProducts.records.forEach((record) => {
            html += `
            <li>
                <a class="klevu-search__product-link" href="${record.url}">
                <div
                    class="klevu-search__product-image"
                    style="background-image: url('${record.image}')"
                >
                    ${this._renderProductBadge(record)}
                </div>
    
                <span class="klevu-search__product-name">
                    ${record.name}
                </span>
    
                <div class="klevu-search__product-price">
                    ${this._renderPrice(record)}
                </div>
                </a>
            </li>
            `
        })

        return html
    }

    /**
         * Build product badge.
         * @param {Object} data - Product data.
         * @returns {String}
         */
    _renderProductBadge (record) {
        let badgeText = ''
        let badgeColour = ''
        let html = ''
        const tagsArray = record.tags.replace('[', '').replace(']', '').split(', ')

        tagsArray.forEach((tag) => {
            if (tag.includes('Badge ')) {
                badgeText = tag.replace('Badge ', '')
            } else if (tag.includes('Badge_Colour ')) {
                badgeColour = tag.replace('Badge_Colour ', '')
            }
        })

        if (badgeText !== '') {
            html += `
            <span
                class="klevu-search__product-badge"
                style="background-color: #${badgeColour}"
            >
                ${badgeText}
            </span>
            `
        }

        return html
    }

    /**
         * Build popular searches list.
         */
    _renderPopularSearches () {
        let html = ''
        const popularTerms = window.theme.popularSearchTerms.list
        const popularTermsArray = popularTerms.split(',')

        popularTermsArray.forEach((item) => {
            html += `
            <li>
                <a
                class="klevu-search__popular-link"
                href="/search?q=${encodeURIComponent(item.trim())}"
                >
                ${item}
                </a>
            </li>
            `
        })

        return html
    }

    /**
         * Build product price.
         * @param {Object} data - Product data.
         * @returns {String}
         */
    _renderPrice (data) {
        let html = ''

        if (data.startPrice) {
            let penceLength = data.startPrice.split('.')
            penceLength = penceLength[1].length

            if (penceLength === 1) {
                data.startPrice += '0'
            }

            html += `
            <span>${formatMoney(data.startPrice, window.theme.moneyFormat)}</span>
            `
        } else if (data.price === data.salePrice) {
            html += `
            <span>${formatMoney(data.salePrice, window.theme.moneyFormat)}</span>
            `
        } else {
            html += `
            <span>${data.salePrice}</span>
            <s>${formatMoney(data.price, window.theme.moneyFormat)}</s>
            <span>${percentageSaving(data.price, data.salePrice, true)}${window.theme.strings.percentageOff}</span>
            `
        }

        return html
    }

    /**
         * Build quick search promo banner.
         * @param {String} type - Promo banner type.
         * @returns {String}
         */
    _renderPromoBox (type) {
        if (!window.theme.quickSearchPromo.status) {
            return ''
        }

        return `
            <a
            class="klevu-search__promo klevu-search__promo--${type}"
            href="${window.theme.quickSearchPromo.promoLink}"
            >
            ${window.theme.quickSearchPromo.promoText}
            </a>
        `
    }

    _sendKlevuAnalyticsData (event) {
        event.preventDefault()
        this._searchTermAnalytics()
        this._clickedProductAnalytics(event)
        window.location.href = event.target.href
    }

    _searchTermAnalytics () {
        const searchDataElement = this._elem.querySelector(this._settings.klevuTotalResults)

        axios({
            method: 'post',
            url: 'https://stats.klevu.com/analytics/n-search/search',
            params: {
                klevu_apiKey: this._searchApikey,
                klevu_term: this._klevuInput.value,
                klevu_totalResults: searchDataElement.dataset.total,
                klevu_typeOfQuery: searchDataElement.dataset.type
            }
        })
            .then(({data}) => {
                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    _clickedProductAnalytics (event) {
        axios({
            method: 'post',
            url: 'https://stats.klevu.com/analytics/productTracking',
            params: {
                klevu_apiKey: this._searchApikey,
                klevu_keywords: this._klevuInput.value,
                klevu_type: 'clicked',
                klevu_productId: event.target.dataset.id,
                klevu_productName: event.target.dataset.name,
                klevu_productUrl: event.target.href
            }
        })
            .then(({data}) => {
                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
   * Expose public interface.
   */
    // return Object.freeze({
    //     init,
    //     hideAllItems,
    //     showAllItems,
    //     hideItem,
    //     showItem,
    // })
}

export default KlevuSearch



// WEBPACK FOOTER //
// ./src/resources/scripts/components/KlevuSearch.js